import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, Phone } from "lucide-react";
import { BLOG_POSTS, COMPANY } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import { PillBadge, CTASection } from "../components/Shared";
import { useQuote } from "../components/QuoteModal";

export default function BlogPost() {
  const { slug } = useParams();
  const ref = useRevealGroup();
  const { openQuote } = useQuote();

  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div ref={ref} key={slug}>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#073B67]">
        <img src={post.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#073B67]/70 to-[#073B67]" />
        <div className="relative mx-auto max-w-4xl px-6 py-20 lg:py-24">
          <div className="hero-stagger">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B9DFF3] transition-colors hover:text-white" data-testid="back-to-blog">
              <ArrowLeft className="h-4 w-4" /> All Insights
            </Link>
            <div className="mt-5"><PillBadge light>{post.category}</PillBadge></div>
            <h1 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl lg:text-[44px]">{post.title}</h1>
            <div className="mt-5 flex items-center gap-5 text-sm text-[#C5DEEC]">
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{post.date}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{post.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-14 px-6 lg:grid-cols-[1fr_320px]">
          <article className="max-w-none" data-reveal>
            <div className="overflow-hidden rounded-3xl">
              <img src={post.image} alt={post.title} className="h-80 w-full object-cover" />
            </div>
            <div className="mt-10 space-y-6">
              {post.content.map((block, i) => {
                if (block.type === "h3") {
                  return <h3 key={i} className="pt-2 font-heading text-xl font-extrabold tracking-tight text-[#0B2748] sm:text-2xl">{block.text}</h3>;
                }
                if (block.type === "ul") {
                  return (
                    <ul key={i} className="space-y-3">
                      {block.items.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-[15.5px] leading-relaxed text-[#294A65]">
                          <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0785C7]" /> {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                return <p key={i} className="text-[15.5px] leading-[1.85] text-[#294A65]">{block.text}</p>;
              })}
            </div>
            <div className="mt-10 rounded-2xl bg-[#F4F7FB] px-6 py-5">
              <p className="text-xs leading-relaxed text-[#70879A]">
                <strong>Disclaimer:</strong> This article is provided for general informational purposes only and does not constitute financial, legal or insurance advice. Program rules, coverage requirements and rates change over time and vary by individual circumstances. Please verify current requirements with official Government of Canada (IRCC) resources and consult a licensed insurance advisor before making decisions.
              </p>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div data-reveal className="rounded-2xl bg-[#006FB9] p-7 text-white">
              <h4 className="font-heading text-lg font-bold">Have questions about this topic?</h4>
              <p className="mt-2 text-sm leading-relaxed text-[#C5DEEC]">Get a free consultation with a licensed advisor — 7 days a week.</p>
              <button onClick={() => openQuote()} className="btn-light group mt-5 w-full justify-center" data-testid="post-sidebar-quote">
                Get a Free Quote <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a href={COMPANY.phoneHref} className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-[#B9DFF3] transition-colors hover:text-white">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
            <div data-reveal className="rounded-2xl border border-[#E1ECF3] p-7">
              <h4 className="font-heading text-base font-bold text-[#0B2748]">Related Reading</h4>
              <div className="mt-4 space-y-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group flex gap-3.5">
                    <img src={r.image} alt={r.title} className="h-16 w-20 shrink-0 rounded-xl object-cover" />
                    <div>
                      <p className="text-sm font-bold leading-snug text-[#193B58] transition-colors duration-300 group-hover:text-[#0785C7]">{r.title}</p>
                      <p className="mt-1 text-xs text-[#70879A]">{r.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <CTASection title="Protect What Matters Most" />
    </div>
  );
}
